import { Grid, List, ToggleButton, ToggleButtonGroup } from '@mui/material';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewListIcon from '@mui/icons-material/ViewList';
import type { Item } from '../../types';
import ItemCard from './ItemCard';
import ItemListRow from './ItemListRow';

export type ViewMode = 'card' | 'list';

export function ViewModeToggle({ value, onChange }: { value: ViewMode; onChange: (mode: ViewMode) => void }) {
  return (
    <ToggleButtonGroup
      value={value}
      exclusive
      onChange={(_e, newMode) => {
        if (newMode !== null) onChange(newMode);
      }}
      size="small"
    >
      <ToggleButton value="card" aria-label="card view">
        <ViewModuleIcon fontSize="small" />
      </ToggleButton>
      <ToggleButton value="list" aria-label="list view">
        <ViewListIcon fontSize="small" />
      </ToggleButton>
    </ToggleButtonGroup>
  );
}

interface ItemsViewProps {
  items: Item[];
  viewMode: ViewMode;
  onClick: (item: Item) => void;
  onEdit?: (item: Item) => void;
  onDelete?: (item: Item) => void;
  onRefresh?: (item: Item) => void;
  onShare?: (item: Item) => void;
  onAddToFolder?: (item: Item) => void;
  onToggleStar?: (item: Item) => void;
  onTagClick?: (tag: string) => void;
  creatorNames?: Record<string, string>;
  /** Use smaller card columns (for panel layouts with less horizontal space) */
  denseGrid?: boolean;
}

export default function ItemsView({
  items,
  viewMode,
  onClick,
  onEdit,
  onDelete,
  onRefresh,
  onShare,
  onAddToFolder,
  onToggleStar,
  onTagClick,
  creatorNames,
  denseGrid,
}: ItemsViewProps) {
  if (viewMode === 'list') {
    return (
      <List disablePadding sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {items.map((item) => (
          <ItemListRow
            key={item.id}
            item={item}
            onClick={onClick}
            onEdit={onEdit}
            onDelete={onDelete}
            onRefresh={onRefresh}
            onShare={onShare}
            onAddToFolder={onAddToFolder}
            onToggleStar={onToggleStar}
            onTagClick={onTagClick}
          />
        ))}
      </List>
    );
  }

  // card mode (default)
  const cardSize = denseGrid
    ? { xs: 12, sm: 6, md: 4, lg: 3 }
    : { xs: 12, sm: 6, md: 4, lg: 3 };
  return (
    <Grid container spacing={denseGrid ? 1 : 2}>
      {items.map((item) => (
        <Grid key={item.id} size={cardSize}>
          <ItemCard
            item={item}
            onClick={onClick}
            onEdit={onEdit}
            onDelete={onDelete}
            onRefresh={onRefresh}
            onShare={onShare}
            onAddToFolder={onAddToFolder}
            onToggleStar={onToggleStar}
            onTagClick={onTagClick}
            creatorName={creatorNames?.[item.createdBy]}
          />
        </Grid>
      ))}
    </Grid>
  );
}
